import cn from "classnames";
import { config as refuelingFormConfig } from "components/bid/show/partials/planRefueling/config";
import CreateRoute from "components/map/createRoute";
import { SidebaredTemplate } from "components/modal/templates";
import { serializeReq, validateForm } from "components/shared/form";
import Loader from "components/shared/loader";
import { isStartBidPoint } from "helpers/bidpoint";
import {
    bidPointTypesEnum,
    bidRefuelingServiceTypesEnum,
    featureModuleTypesEnum,
    mapObjectTypesEnum,
} from "helpers/enums";
import { bidPointsToMapData, clearGasStations, id, metersToReadableDistance } from "helpers/map";
import { paths } from "helpers/paths";
import { getClientRouteOptions, isRegionDryingPeriodsEnabledForClients } from "helpers/regionDryingPeriods";
import { arraysEqual, formatNumber, isDesktop } from "helpers/system";
import Tooltip from "rc-tooltip";
import React from "react";
import { CustomScroll } from "react-custom-scroll";
import { alert } from "services/alert";
import { calculatePeriodsForPoints } from "sources/books/bids";
import { addToPlanQueue } from "sources/books/refueling";
import { createDistributionBidRoute, createRoute, geocoder, getRoute } from "sources/map";
import Panel from "./panel";
import RefuelingForm from "./refuelingForm";
import RouteSettings from "./settings";

const VIEWPORTTABLET = 801;

class RouteModal extends SidebaredTemplate {
    prevSegmentIds = [];

    initialState = {
        viewport: {
            zoom: 6,
            center: [55.7473, 52.4568],
        },
        elements: [],
        segments: [],
        tollRoadSegments: [],
        activeElements: {
            point: null,
            segments: null,
        },
        mapClickDisabled: false,
        isFetching: false,
        isFetchingDelete: false,
        showPanel: this.props.confirmRoute,
        refuelingFormData: { fuelTanks: [] },
        refuelingFormValid: true,
        routeTemplateId: null,
        routeChanged: false,
        routeOptionsOpened: false,
        regionDryingPeriodIds: [],
        routeOptions: {
            disallowToll: false,
            disallowTollExceptM4: false,
            disallowTollExceptM12DRT: false,
            avoidSpecialRoadFlags: 0,
            avoidTollRoadFlags: 0,
            allowFerry: false,
            routerProfile: null,
            country: null,
        },
    };

    shouldBeResponsive = () => window.innerWidth <= VIEWPORTTABLET;

    UNSAFE_componentWillMount() {
        this.setState({ showPanel: this.shouldBeResponsive() });
        window.addEventListener("resize", this.handleWindowRezise);
    }

    componentWillUnmount() {
        window.removeEventListener("resize", this.handleWindowRezise);
    }

    handleWindowRezise = () => this.setState({ showPanel: this.shouldBeResponsive() });

    componentDidMount() {
        const {
            bidPoints,
            refueling,
            itemChangePoints,
            routeControlPoints,
            defaultRouteOptions,
            route: { segments, routeOptions, tollRoadSegments },
        } = this.props;
        const nextState = {
            ...bidPointsToMapData(
                bidPoints,
                segments,
                refueling,
                itemChangePoints,
                tollRoadSegments,
                routeControlPoints,
            ),
            ...{ routeOptions: getClientRouteOptions(routeOptions || defaultRouteOptions) },
        };
        this.setState({ ...this.initialState, ...nextState, isLoading: false });
    }

    componentDidUpdate() {
        if (
            !arraysEqual(
                this.prevSegmentIds,
                this.state.segments?.map((item) => item.i),
            )
        ) {
            this.prevSegmentIds = this.state.segments?.map((item) => item.i);
            if (!isRegionDryingPeriodsEnabledForClients) {
                return;
            }
            const { bidPoints } = this.props;
            const from = bidPoints?.[0]?.planLeaveDateOffset || bidPoints?.[0]?.planEnterDateOffset;
            const to = bidPoints?.[bidPoints.length - 1]?.planEnterDateOffset;
            const points = this.state.segments?.reduce(
                (acc, item) => [...acc, ...item.coords.map((item) => [item[1], item[0]])],
                [],
            );
            if (from && to && points) {
                calculatePeriodsForPoints({ from, to, points }).then(
                    (regionDryingPeriodIds) => regionDryingPeriodIds && this.setState({ regionDryingPeriodIds }),
                );
            }
        }
    }

    mergeMapViewport = (viewport) => {
        this.setState({ viewport });
    };

    changeActiveElements = (nextState) => {
        this.setState(nextState);
        const reqs = (nextState.segments || this.state.segments).reduce((acc, item) => {
            if (item.needCalculate) {
                const { start, end } = item.geozone;
                acc.push(
                    getRoute([start.location.coordinates, end.location.coordinates], {
                        silent: true,
                        routeOptions: this.state.routeOptions,
                    }).then((data) => {
                        if (!data) {
                            return false;
                        }
                        const { concatedCoords, routeInfo, roadRestrictions, roadControlPoints } = data || {};
                        return {
                            routeType: item.routeType,
                            coords: concatedCoords || [],
                            roadRestrictions,
                            roadControlPoints: roadControlPoints || [],
                            routeInfo: {
                                ...(routeInfo || {}),
                                tollSegments: routeInfo?.tollSegments?.map((item) => ({
                                    ...item,
                                    segmentIndex: item.i,
                                })),
                            },
                            i: item.i,
                        };
                    }),
                );
            }
            return acc;
        }, []);
        if (reqs.length > 0) {
            this.setState({ isLoading: true });
            Promise.all(reqs).then((newSegments) => {
                const ids = newSegments.map((item) => item.i);
                const newSegmentIds = [];
                const segments = (nextState.segments || this.state.segments).map((segment) => {
                    const item = ids.includes(segment.i)
                        ? newSegments.find((newSegment) => newSegment.i === segment.i)
                        : segment;
                    newSegmentIds.push(item.i);
                    return item;
                });
                const elements = (nextState.elements || this.state.elements).map((element) => {
                    if (!ids.includes(element.i)) {
                        return element;
                    }
                    const segmentElement = newSegments.find((newSegment) => newSegment.i === element.i);
                    return { ...segmentElement, geozone: element.geozone, panelType: "segment" };
                });
                const nextTollRoadSegments = (nextState.tollRoadSegments || this.state.tollRoadSegments).filter(
                    (item) => !newSegmentIds.includes(item.segmentIndex),
                );
                newSegments.forEach((element) => {
                    nextTollRoadSegments.push(...(element.routeInfo.tollSegments || []));
                });
                this.changeActiveElements({
                    elements,
                    segments,
                    tollRoadSegments: nextTollRoadSegments,
                    isLoading: false,
                });
            });
        }
    };

    getRouteWithoutElement = (props) => {
        const { elements, item, routeOptions } = props;
        const elementIndex = elements.findIndex((element) => element.i === item.i);
        const start = elements[elementIndex - 2];
        const end = elements[elementIndex + 2];
        const segmentCoords = [start.geozone.location.coordinates, end.geozone.location.coordinates];
        return getRoute(segmentCoords, { routeOptions }).then((data) => {
            const segmentId = id();
            return {
                coords: data?.concatedCoords || [],
                roadRestrictions: data?.roadRestrictions,
                roadControlPoints: data?.roadControlPoints || [],
                routeInfo: {
                    ...(data?.routeInfo || {}),
                    tollSegments: data?.routeInfo?.tollSegments?.map((item) => ({ ...item, segmentIndex: segmentId })),
                },
                start,
                end,
                i: segmentId,
            };
        });
    };

    calcDeleted = ({ coords, start, end, i, routeInfo, prevSegment, roadRestrictions, roadControlPoints }) => {
        const { elements, segments, markers, tollRoadSegments } = clearGasStations({ ...this.state });
        const elemIndex = elements.findIndex((item) => item.i === start.i);
        const segmIndex = segments.findIndex((item) => item.i === elements[elemIndex + 1].i);
        const nextElem = {
            geozone: { start: start.geozone, end: end.geozone },
            panelType: "segment",
            i,
            coords,
            routeInfo,
            routeType: prevSegment?.routeType,
        };
        const filteredMarkers = markers.filter((item) => item.i !== elements[elemIndex + 2].i);
        const activeElements = { point: null, segments: null };
        const removeSegmentIds = [segments[segmIndex].i, segments[segmIndex + 1].i];
        elements.splice(elemIndex + 1, 3, nextElem);
        segments.splice(segmIndex, 2, {
            routeInfo,
            coords,
            i,
            routeType: prevSegment?.routeType,
            roadRestrictions,
            roadControlPoints: roadControlPoints || [],
        });

        const nextTollRoadSegments = tollRoadSegments.filter((item) => !removeSegmentIds.includes(item.segmentIndex));
        nextTollRoadSegments.push(...(routeInfo.tollSegments || []));
        return this.changeActiveElements({
            elements,
            segments,
            activeElements,
            tollRoadSegments: nextTollRoadSegments,
            markers: filteredMarkers,
            routeChanged: true,
        });
    };

    setFetchingDelete = (value) => {
        this.setState({ isFetchingDelete: value });
    };

    renderPanel = (item) => {
        const { activeElements, elements, prevSegment, segments, markers } = this.state;
        const props = {
            item,
            elements,
            activeElements,
            prevSegment,
            segments,
            markers,
            index: this.bidPointIndex,
            firstLoadPointIndex: this.firstLoadPointIndex,
            isFetchingDelete: this.state.isFetchingDelete,
            mergeVp: this.mergeMapViewport,
            changeAe: this.changeActiveElements,
            getRouteWithoutElement: this.getRouteWithoutElement,
            calcDeleted: this.calcDeleted,
            setFetchingDelete: this.setFetchingDelete,
        };
        if (item.panelType === "bidpoint") {
            !isStartBidPoint(item.geozone.type) && this.bidPointIndex++;
        }
        return <Panel key={item.i} routeOptions={this.state.routeOptions} {...props} />;
    };

    getMapObject = ({ geozone }) => ({
        id: geozone.id,
        type: geozone.type,
        radius: geozone.radius,
        address: geozone.address,
        location: {
            type: "Point",
            coordinates: geozone.location.coordinates,
        },
    });

    resetSupportPoints = () => {
        this.setState({ isLoading: true });
        const { elements, markers } = this.state;
        const newMarkers = markers.filter((m) => ["bidpoint", "gasstation"].includes(m.panelType));
        newMarkers.forEach((m, i) => {
            m.index = i;
        });

        const pointElems = elements.filter((m) => ["bidpoint", "gasstation"].includes(m.panelType));
        const newElements = [];
        const segmentElements = [];
        pointElems.forEach((elem, i) => {
            newElements.push(elem);
            const nextElem = pointElems[i + 1];
            if (nextElem) {
                const newSegment = this.segmentElem(this.getMapObject(elem), this.getMapObject(nextElem));
                newElements.push(newSegment);
                segmentElements.push(newSegment);
            }
        });

        Promise.all(this.segmentRequests(segmentElements)).then((newSegments) => {
            const nextTollRoadSegments = [];
            newSegments.forEach((element) => {
                nextTollRoadSegments.push(...(element.routeInfo.tollSegments || []));
            });
            this.setState({
                elements: newElements,
                markers: newMarkers,
                segments: newSegments,
                tollRoadSegments: nextTollRoadSegments,
                isLoading: false,
                routeTemplateId: null,
            });
        });
    };

    segmentRequests = (segmentElements) =>
        segmentElements.map(({ i, geozone }) => {
            const segmentCoords = [geozone.start.location.coordinates, geozone.end.location.coordinates];
            return getRoute(segmentCoords, { routeOptions: this.state.routeOptions }).then((data) => ({
                coords: data?.concatedCoords || [],
                roadRestrictions: data?.roadRestrictions,
                roadControlPoints: data?.roadControlPoints || [],
                routeInfo: {
                    ...(data?.routeInfo || {}),
                    tollSegments: data?.routeInfo?.tollSegments?.map((item) => ({ ...item, segmentIndex: i })),
                },
                i,
            }));
        });

    segmentElem = (start, end) => ({
        geozone: { start, end },
        i: id(),
        panelType: "segment",
    });

    betweenLoadUnloadPointIndexes = () => {
        const { markers, elements } = this.state;
        let loadMarkerIndex;
        let unloadMarkerIndex;
        let loadMarker;
        let unloadMarker;
        for (let i = 0; i < markers.length; i++) {
            if (markers[i].type === bidPointTypesEnum.LoadPoint) {
                loadMarker = markers[i];
                loadMarkerIndex = i;
            }
            if (markers[i].type === bidPointTypesEnum.UnloadPoint) {
                unloadMarker = markers[i];
                unloadMarkerIndex = i;
                break;
            }
        }
        const loadElemIndex = elements.findIndex((element) => element.i === loadMarker.i);
        const unloadElemIndex = elements.findIndex((element) => element.i === unloadMarker.i);
        return { loadMarkerIndex, unloadMarkerIndex, loadMarker, unloadMarker, loadElemIndex, unloadElemIndex };
    };

    buildElemsByRouteTemplate = (newMarkers, loadMarker, unloadMarker) => {
        const newElements = [];
        const segment = this.segmentElem(loadMarker.geozone, newMarkers[0].geozone);
        newElements.push(segment);
        for (let i = 0; i < newMarkers.length; i++) {
            newElements.push(newMarkers[i]);
            const segment = newMarkers[i + 1]
                ? this.segmentElem(newMarkers[i].geozone, newMarkers[i + 1].geozone)
                : this.segmentElem(newMarkers[i].geozone, unloadMarker.geozone);
            newElements.push(segment);
        }
        return newElements;
    };

    handleRouteTemplate = (supportPoints) => {
        if (!supportPoints) {
            return this.resetSupportPoints();
        }

        this.setState({ isLoading: true });
        const geocoderReq = supportPoints.map(([lng, lat]) => geocoder({ lat, lng }));
        Promise.all(geocoderReq).then((response) => {
            const { markers, elements } = this.state;
            const { loadMarkerIndex, unloadMarkerIndex, loadMarker, unloadMarker, loadElemIndex, unloadElemIndex } =
                this.betweenLoadUnloadPointIndexes();

            const newMarkers = response.map((geozone) => ({ geozone, i: id(), panelType: "geopoint" }));
            const newElements = this.buildElemsByRouteTemplate(newMarkers, loadMarker, unloadMarker);

            markers.splice(loadMarkerIndex + 1, unloadMarkerIndex - loadMarkerIndex - 1, ...newMarkers);
            elements.splice(loadElemIndex + 1, unloadElemIndex - loadElemIndex - 1, ...newElements);
            const segmentElems = elements.filter((elem) => elem.panelType === "segment");
            Promise.all(this.segmentRequests(segmentElems)).then((segments) => {
                segments.forEach((s) => {
                    elements.find((el) => el.i === s.i).coords = s.coords;
                });
                const nextTollRoadSegments = [];
                segments.forEach((element) => {
                    nextTollRoadSegments.push(...(element.routeInfo.tollSegments || []));
                });
                this.setState({
                    elements,
                    markers,
                    segments,
                    tollRoadSegments: nextTollRoadSegments,
                    isLoading: false,
                    routeChanged: true,
                });
            });
        });
    };

    createRoute = (canCreate) => {
        if (!canCreate) {
            return;
        }
        this.setState({ isFetching: true });
        const { bidId, kind } = this.props;
        const segments = this.state.elements.filter((item) => item.panelType === "segment");
        const segmentsKeys = segments.map(({ geozone }, index) => {
            const { start, end } = geozone;
            return { mapObjectFrom: start, mapObjectTo: end, order: index };
        });
        const req = kind === "distributionBid" ? createDistributionBidRoute : createRoute;
        return req({
            bidId,
            segmentsKeys,
            routeIndex: 0,
            routeOptions: this.state.routeOptions,
        }).then(() => {
            this.setState({ isFetching: false });
            this.props.close();
        });
    };

    startBid = (canCreate) => {
        if (!canCreate) {
            return;
        }
        const { onSubmitAndStart, refuelingSettings } = this.props;
        const { carLinked, refuelingServiceType, ...refuelingFormData } = this.state.refuelingFormData;
        const carSensorMonitoringModuleEnabled = this.props.availableFeatureModules.includes(
            featureModuleTypesEnum.CarSensorMonitoring,
        );
        if (
            !this.props.availableFeatureModules.includes(featureModuleTypesEnum.RefuelingPlanning) ||
            refuelingSettings.hideRefuelingPlanning ||
            ((refuelingServiceType === bidRefuelingServiceTypesEnum.MultiGo ||
                refuelingServiceType === bidRefuelingServiceTypesEnum.MultiGo2) &&
                !carLinked)
        ) {
            this.createRoute(true).then(onSubmitAndStart);
        } else {
            if (carSensorMonitoringModuleEnabled) {
                this.createRoute(true).then(onSubmitAndStart);
            } else if (validateForm(refuelingFormConfig, serializeReq(refuelingFormData))) {
                const { bidId } = this.props;
                const stateData = { ...serializeReq(refuelingFormData), bidId };
                addToPlanQueue(stateData).then(
                    (res) => res && alert({ message: "Запущен процесс планирования заправок.", type: "success" }),
                );
                this.createRoute(true).then(onSubmitAndStart);
            } else if (
                confirm("Вы не заполнили данные для планирования заправок, все равно запустить заявку в работу?")
            ) {
                this.createRoute(true).then(onSubmitAndStart);
            } else {
                this.setState({ refuelingFormValid: false });
            }
        }
    };

    closeModal = () => {
        const { confirmRoute, history, bidId, fromShowPage } = this.props;
        this.close();
        confirmRoute && fromShowPage && history.push(paths.bidShow("bid", bidId));
    };

    renderHeader = () => {
        const { title, close } = this.props;
        const cname = cn("asided-modal__header--bold", {
            "asided-modal__header--small": !isDesktop(),
        });
        return (
            <div className="modal-window__header">
                <div className="modal-window__header-title--center">
                    {this.props.confirmRoute ? (
                        <div className={cname}>Убедитесь в корректности маршрута</div>
                    ) : (
                        title || ""
                    )}
                </div>
                <div className="close close--sm close--mobile" onClick={close} />
            </div>
        );
    };

    refreshSegmentRoutes = () => {
        this.setState({ isLoading: true });
        const segmentElems = this.state.elements.filter((elem) => elem.panelType === "segment");
        Promise.all(this.segmentRequests(segmentElems)).then((segments) => {
            const nextTollRoadSegments = [];
            segments.forEach((element) => {
                nextTollRoadSegments.push(...(element.routeInfo.tollSegments || []));
            });
            this.setState({ segments, tollRoadSegments: nextTollRoadSegments, isLoading: false, routeChanged: true });
        });
    };

    renderOptions = () => {
        const { defaultRouteOptions, availableFeatureModules, refuelingServiceType } = this.props;
        const { routeOptions, routeTemplateId, routeOptionsOpened } = this.state;
        const props = {
            defaultRouteOptions,
            availableFeatureModules,
            routeOptions,
            routeTemplateId,
            routeOptionsOpened,
            refuelingServiceType,
            updateSettings: this.changeActiveElements,
            refreshSegmentRoutes: this.refreshSegmentRoutes,
            handleRouteTemplate: this.handleRouteTemplate,
            modal: this.props.add,
        };
        return <RouteSettings {...props} />;
    };

    renderRefueling = () => {
        if (
            !this.props.availableFeatureModules.includes(featureModuleTypesEnum.RefuelingPlanning) ||
            this.props.refuelingSettings.hideRefuelingPlanning
        ) {
            return null;
        }
        if (!this.props.confirmRoute) {
            return null;
        }
        const { carOption } = this.props;
        const setValue = (refuelingFormData) => this.setState({ refuelingFormData });
        return (
            <RefuelingForm
                data={this.state.refuelingFormData}
                setValue={setValue}
                bid={{ car: carOption }}
                isValid={this.state.refuelingFormValid}
            />
        );
    };

    renderBody = () => {
        this.bidPointIndex = 0;
        this.firstLoadPointIndex = -1;

        const { elements, isLoading, isFetching, segments, markers, tollRoadSegments } = this.state;
        elements?.some((item) => {
            if (item.panelType === "bidpoint" && item.geozone.type !== mapObjectTypesEnum.StartBidPoint) {
                this.firstLoadPointIndex++;
            }
            return [bidPointTypesEnum.LoadPoint, bidPointTypesEnum.UnloadPoint].includes(item.type);
        });
        const { confirmRoute, availableFeatureModules, isFirstBid } = this.props;
        const noActiveEl =
            this.state.activeElements &&
            this.state.activeElements.point === null &&
            this.state.activeElements.segments === null;
        const cname = (mainClass) => cn(mainClass, "btn btn-sm", { disabled: !noActiveEl });
        let distanceSum;
        if (segments) {
            distanceSum = segments.reduce((prev, cur) => prev + cur.routeInfo.distance || 0, 0);
        }
        const distanceStr = metersToReadableDistance(
            distanceSum,
            (tollRoadSegments || []).reduce((sum, item) => sum + item.cost || 0, 0),
        );
        const hasSupportPoints = markers && markers.some((m) => !["bidpoint", "gasstation"].includes(m.panelType));

        const groupedTollRoads = (tollRoadSegments || []).reduce((acc, item) => {
            if (!acc[item.tag]) {
                acc[item.tag] = { ...item };
            } else {
                acc[item.tag].distance += item.distance;
                acc[item.tag].cost += item.cost;
            }
            return acc;
        }, {});

        return isLoading ? (
            <Loader prefix="absolute-center" />
        ) : (
            <div className="map-panels">
                <div className="map-panels__content">
                    <CustomScroll heightRelativeToParent="100%">
                        {elements.map(this.renderPanel)}
                        {tollRoadSegments?.length > 0 && (
                            <div className="font-size-14 p-3 border-bottom border-secondary">
                                <div className="pb-2 font-weight-bold">
                                    <i className="icon-uEA13-rubl pr-2" style={{ position: "relative", top: "1px" }} />{" "}
                                    Платные участки дороги
                                </div>
                                {Object.values(groupedTollRoads).map((segment, i) => (
                                    <div key={i} className="pl-3 position-relative mb-2">
                                        <div className="paid-line" style={{ backgroundColor: segment.color }} />
                                        {segment.trackName || "Нет информации о стоимости"} <br />
                                        <span className="text-muted">
                                            {formatNumber(Math.round(segment.distance / 1000))} км&nbsp;
                                            {formatNumber(segment.cost, { style: "currency", currency: "RUB" })}
                                        </span>
                                    </div>
                                ))}
                            </div>
                        )}
                        <div className="f-route__info">
                            <div className="f-route__info--duration-block" />
                            <div className="f-route__info--distance-block">{distanceStr}</div>
                        </div>
                        {!availableFeatureModules.includes(featureModuleTypesEnum.CarSensorMonitoring) &&
                            this.renderRefueling()}
                    </CustomScroll>
                </div>
                <div className="map-panels__footer">
                    {isFetching ? (
                        <Loader />
                    ) : (
                        <>
                            {this.renderOptions()}
                            <div className="map-panels__actions">
                                {confirmRoute ? (
                                    isFirstBid ? (
                                        <Tooltip
                                            visible
                                            overlay={
                                                <span className="p-2 font-size-14">
                                                    Теперь запустите заявку в работу
                                                </span>
                                            }
                                            placement="top"
                                        >
                                            <div
                                                className="btn-brand btn btn-sm ts-chat-pulse"
                                                onClick={() => this.startBid(noActiveEl)}
                                            >
                                                В работу
                                            </div>
                                        </Tooltip>
                                    ) : (
                                        <div className="btn-brand btn btn-sm" onClick={() => this.startBid(noActiveEl)}>
                                            В работу
                                        </div>
                                    )
                                ) : (
                                    <div className={cname("btn-brand")} onClick={() => this.createRoute(noActiveEl)}>
                                        Сохранить
                                    </div>
                                )}
                                <div>
                                    {hasSupportPoints && (
                                        <div
                                            className="btn btn-sm btn-light mr-2"
                                            onClick={() => this.resetSupportPoints()}
                                        >
                                            Сбросить точки
                                        </div>
                                    )}
                                    <div className="btn-secondary btn btn-sm" onClick={this.closeModal}>
                                        Отмена
                                    </div>
                                </div>
                            </div>
                        </>
                    )}
                </div>
            </div>
        );
    };

    calcCname = () => {
        const { windowMod, confirmRoute } = this.props;
        return cn({
            "modal__window modal-window modal-window--lg modal-window--asided asided-modal": true,
            "asided-modal--confirmable": confirmRoute && isDesktop(),
            "asided-modal--collapsed": !this.state.sidebarOpened && !isDesktop(),
            [`modal-window--${windowMod}`]: !!windowMod,
        });
    };

    renderMap = () => {
        const { tileUrls } = this.props;
        const {
            viewport,
            segments,
            markers,
            activeElements,
            elements,
            sidebarOpened,
            mapClickDisabled,
            tollRoadSegments,
        } = this.state;
        if (!viewport) {
            return null;
        }
        const routeControlPoints = (segments || []).flatMap((s) => s.roadControlPoints || []);
        return (
            <CreateRoute
                routeOptions={this.state.routeOptions}
                markersWithoutTooltips
                changeMapPosition={this.state.changeMapPosition}
                regionDryingPeriodIds={this.state.regionDryingPeriodIds}
                {...this.props}
                segments={segments}
                tollRoadSegments={tollRoadSegments}
                navElements={elements}
                markers={markers}
                tileUrls={tileUrls}
                viewport={viewport}
                hideZoomControl={true}
                mergeMapViewport={this.mergeMapViewport}
                activeElements={activeElements}
                changeActiveElements={this.changeActiveElements}
                shouldResize={sidebarOpened}
                mapClickDisabled={mapClickDisabled}
                mergeVp={this.mergeMapViewport}
                getRouteWithoutElement={this.getRouteWithoutElement}
                calcDeleted={this.calcDeleted}
                disableMapClick={() => this.setState({ mapClickDisabled: true })}
                routeControlPoints={routeControlPoints}
            />
        );
    };

    render() {
        const cname = this.calcCname();
        const { showPanel } = this.state;
        const cnameTogglePanelBtn = cn("asided-modal__showpanel-btn", {
            "asided-modal__showpanel-btn--close": !showPanel,
        });
        return (
            <div className={cname}>
                {isDesktop() === false && (
                    <div
                        className={cnameTogglePanelBtn}
                        onClick={() => {
                            this.setState({ showPanel: !this.state.showPanel });
                            // хак для того, чтобы панель выглядила нормально без правого падинга
                            setTimeout(() => !this.state.showPanel && this.setState({ changeMapPosition: true }), 0);
                        }}
                    />
                )}
                <div className="asided-modal__aside">
                    {this.renderHeader()}
                    {this.renderBody()}
                </div>
                <div id="modal-map" className="asided-modal__content map">
                    {this.renderMap()}
                </div>
            </div>
        );
    }
}

export default RouteModal;
